import { useAuth0 } from "@auth0/auth0-react";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import RestoreIcon from "@mui/icons-material/Restore";
import { Box, Divider, TableRow, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router";

import { Loader, MenuItem, Modal, TableList, TableMenu } from "@/atoms";
import {
    DistributionDetailsDrawer,
    DistributionDrawer,
    EditReleaseDistributionForm,
    Filters,
    PageHead,
} from "@/components";
import {
    actionsData,
    distributionActionsData,
    filtersModulesData,
    identifiersData,
    namesData,
    permissionsData,
    selectionsData,
    statusTypesData,
    tableHeadCellsData,
    urlsData,
} from "@/data";
import {
    useDeleteDistribution,
    useDistributionsList,
    useRetryFailedDistribution,
    useTakeDownAppleDistribution,
} from "@/hooks";
import {
    clearFilters,
    closeFilters,
    setModulesFilters,
    toggleDistributionDetailsDrawer,
    toggleDistributionDrawer,
} from "@/redux/actions";
import { StyledLink, StyledProgressBar, StyledTableCell } from "@/styles";
import StyledChip from "@/styles/chip";
import { canDo, convertDateToLocaleStringFormatHandler, convertHourToDurationFormatHandler } from "@/utils";

const DistributionsList = ({ releaseId }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const location = useLocation();

    const filtersModules = useSelector((state) => state.filtersReducer?.filtersModules);

    const { distributionTypes: distributionTypesSelectionData } = selectionsData.data;

    const [releaseDistributionTypeOption, videoDistributionTypeOption] = distributionTypesSelectionData;

    const { tracks: tracksDistributionAction, video: videoDistributionAction } = distributionActionsData;

    const distributionTypesActions = {
        [releaseDistributionTypeOption.value]: tracksDistributionAction,
        [videoDistributionTypeOption.value]: videoDistributionAction,
    };

    const selectedDistributionTypes = filtersModules?.distributions?.distributionType?.in || [];

    // `listDistributions` filters on a single action, so selecting both types is the same as no filter.
    const distributionActionFilter =
        selectedDistributionTypes.length === 1 ? distributionTypesActions[selectedDistributionTypes[0]] : undefined;

    const {
        count,
        data,
        isRefetching,
        limit,
        loading,
        nextCursor,
        page,
        prevCursor,
        setCursor,
        setLimit,
        setPage,
        startPolling,
        stopPolling,
    } = useDistributionsList(parseInt(releaseId), distributionActionFilter);

    const filtersOpened = useSelector((state) => state.filtersReducer.open);

    const { retryFailedDistribution } = useRetryFailedDistribution(parseInt(releaseId), limit, page);

    const { takeDownAppleDistribution } = useTakeDownAppleDistribution(parseInt(releaseId), limit, page);

    const { deleteDistribution } = useDeleteDistribution(parseInt(releaseId), limit, page);

    const { user } = useAuth0();

    const { active, delayed, waiting } = statusTypesData;

    const [selectedDistribution, setSelectedDistribution] = useState(null);

    const [modalData, setModalData] = useState({
        action: deleteDistribution,
        actionType: actionsData.edit,
        body: null,
        id: null,
        open: false,
        title: t("releases.editDistribution"),
    });

    const [takeDownModalData, setTakeDownModalData] = useState({
        action: takeDownAppleDistribution,
        actionType: actionsData.takeDown,
        body: t("messages.takeDownMsgs.alert", { entity: `${t("releases.entity")} ${t("labels.distribution")}` }),
        id: null,
        open: false,
        title: t("releases.takeDownDistribution"),
    });

    const { mediaCreate: mediaCreatePermission, mediaRead: mediaReadPermission } = permissionsData.permissions.keys;

    const { menu: menuId, option: optionId } = identifiersData.ids;

    const { values } = namesData;

    const {
        menus: { table: tableMenuName },
        options: { retryDistribution: retryDistributionOptionName },
    } = namesData;

    const filtersLength = Object.keys(filtersModules?.distributions || {}).length;

    const hasActiveJobs = data?.some(
        (distribution) =>
            distribution.status === active || distribution.status === waiting || distribution.status === delayed,
    );

    useEffect(
        () => {
            if (releaseId) return;

            dispatch(setModulesFilters({}));

            dispatch(clearFilters());

            dispatch(closeFilters());
        },
        [], // eslint-disable-line
    );

    useEffect(
        () => {
            if (data && hasActiveJobs) startPolling(7000);
            else if (data && !hasActiveJobs) stopPolling();

            return () => stopPolling();
        },
        [hasActiveJobs], // eslint-disable-line
    );

    if (loading && !data) return <Loader withoutFullHeight={!!releaseId} />;

    return (
        <Box>
            <Modal modalData={modalData} setModalData={setModalData} />
            <Modal modalData={takeDownModalData} setModalData={setTakeDownModalData} />
            <DistributionDrawer limit={limit} page={page} hasRefetchDistributions />
            <DistributionDetailsDrawer distribution={selectedDistribution} />
            <PageHead
                addAction={() => dispatch(toggleDistributionDrawer(true))}
                filtersCount={filtersLength}
                filtersModule={filtersModulesData.distributions}
                hasAdd={!!releaseId}
                hasFilters={!releaseId}
                title={t("distributions.name")}
                titleTag={releaseId && "h2"}
            />
            {!releaseId && filtersOpened && <Filters loadings={{}} module={filtersModulesData.distributions} />}
            <TableList
                count={count}
                headCells={tableHeadCellsData.releases.distributions}
                isLoadingRows={isRefetching}
                limit={limit}
                page={page}
                setLimit={setLimit}
                setPage={(pageNumber) => {
                    setPage(pageNumber);

                    if (pageNumber > page) setCursor(nextCursor);
                    else setCursor(prevCursor);
                }}
            >
                {data?.map(
                    (
                        {
                            client,
                            distributionAction,
                            finishedOn,
                            id: distributionId,
                            isTakedown,
                            logFilePath,
                            processedOn,
                            progress,
                            releaseId: distributionReleaseId,
                            releaseName,
                            status,
                            takedownLogs,
                            territory,
                            timestamp,
                            trackIsrc,
                            videoIsrc,
                        },
                        index,
                    ) => (
                        <TableRow
                            key={distributionId}
                            sx={{ cursor: distributionAction === videoDistributionAction ? "pointer" : "default" }}
                            hover
                            onClick={() => {
                                if (distributionAction !== videoDistributionAction) return;

                                // Snapshot of the row so the drawer renders instantly while `getDistributionJob` loads.
                                setSelectedDistribution({
                                    client,
                                    distributionAction,
                                    distributionType: t(`labels.${videoDistributionTypeOption.value}`),
                                    finishedOn,
                                    id: distributionId,
                                    isTakedown,
                                    logFilePath,
                                    processedOn,
                                    releaseName,
                                    status,
                                    territory,
                                    timestamp,
                                    trackIsrc,
                                    videoIsrc,
                                });

                                dispatch(toggleDistributionDetailsDrawer(true));
                            }}
                        >
                            <StyledTableCell boldCell={+true}>{releaseName}</StyledTableCell>
                            <StyledTableCell boldCell={+true}>{client.name}</StyledTableCell>
                            <StyledTableCell boldCell={+true}>
                                {territory.name === values.deletedTerritories ? (
                                    territory.name
                                ) : (
                                    <StyledLink
                                        id={`${territory.name}_${index + 1}`}
                                        state={{ from: location.pathname + location.search }}
                                        to={`${urlsData.routes.territories.edit}/${territory?.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {territory.name}
                                    </StyledLink>
                                )}
                            </StyledTableCell>
                            <StyledTableCell>
                                {t(
                                    `labels.${
                                        distributionAction === videoDistributionAction
                                            ? videoDistributionTypeOption.value
                                            : releaseDistributionTypeOption.value
                                    }`,
                                )}
                            </StyledTableCell>
                            <StyledTableCell>
                                <time>{convertDateToLocaleStringFormatHandler(processedOn)}</time>
                            </StyledTableCell>
                            <StyledTableCell>
                                {status === "active" ? (
                                    t("labels.N/A")
                                ) : (
                                    <time>
                                        {convertHourToDurationFormatHandler(
                                            (new Date(finishedOn).getTime() - new Date(processedOn).getTime()) / 1000,
                                        )}
                                    </time>
                                )}
                            </StyledTableCell>
                            <StyledTableCell>
                                <StyledChip label={status} status={status} />
                            </StyledTableCell>
                            <StyledTableCell>
                                <Box
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        gap: 2,
                                    }}
                                >
                                    <StyledProgressBar
                                        status={status}
                                        value={status !== "waiting" ? parseInt(progress) : 0}
                                        variant={status === "active" ? "indeterminate" : "determinate"}
                                    />
                                    <Typography variant="body2">
                                        {status !== "waiting" ? parseInt(progress) : 0}%
                                    </Typography>
                                </Box>
                            </StyledTableCell>
                            <StyledTableCell onClick={(e) => e.stopPropagation()}>
                                <TableMenu
                                    disabled={status === "active"}
                                    rowId={index + 1}
                                    setOpenModal={() => {
                                        setModalData({
                                            ...modalData,
                                            id: distributionId,
                                            open: true,
                                        });
                                    }}
                                >
                                    <MenuItem
                                        icon={<ReplayIcon />}
                                        id={`${tableMenuName}_${menuId}_${index + 1}_${retryDistributionOptionName}_${optionId}`}
                                        label={t("actions.retry")}
                                        disabled={
                                            progress === 100 ||
                                            status === "waiting" ||
                                            !canDo(user, [mediaCreatePermission])
                                        }
                                        onClick={() => retryFailedDistribution(parseInt(distributionId))}
                                    />

                                    {(!!client.isApple || client.name?.toLowerCase().includes("facebook")) &&
                                        import.meta.env.VITE_ENABLE_APPLE_DISTRIBUTION_EDIT === "true" && (
                                            <MenuItem
                                                icon={<EditOutlinedIcon />}
                                                id={`${tableMenuName}_${menuId}_${index + 2}_${retryDistributionOptionName}_${optionId}`}
                                                label={t("actions.edit")}
                                                disabled={
                                                    progress !== 100 ||
                                                    status === "waiting" ||
                                                    !canDo(user, [mediaCreatePermission])
                                                }
                                                onClick={() => {
                                                    setModalData({
                                                        ...modalData,
                                                        body: (
                                                            <EditReleaseDistributionForm
                                                                clientId={client.id}
                                                                jobId={distributionId}
                                                                releaseId={distributionReleaseId}
                                                                setModalData={setModalData}
                                                                territoryId={territory.id}
                                                            />
                                                        ),
                                                        distributionId,
                                                        hasComponent: true,
                                                        open: true,
                                                    });
                                                }}
                                            />
                                        )}
                                    {!!client.isApple && logFilePath && (
                                        <Link
                                            style={{ textDecoration: "none" }}
                                            target="_blank"
                                            to={`${import.meta.env.VITE_BACKEND_DOMAIN}${logFilePath}`}
                                        >
                                            <MenuItem
                                                icon={<RestoreIcon />}
                                                id={`${tableMenuName}_${menuId}_${index + 1}_${retryDistributionOptionName}_${optionId}`}
                                                label={t("releases.downloadLogFile")}
                                                disabled={!canDo(user, [mediaReadPermission])}
                                            />
                                        </Link>
                                    )}
                                    {!!client.isApple && takedownLogs && (
                                        <Link
                                            style={{ textDecoration: "none" }}
                                            target="_blank"
                                            to={`${import.meta.env.VITE_BACKEND_DOMAIN}${takedownLogs}`}
                                        >
                                            <MenuItem
                                                icon={<DownloadIcon />}
                                                id={`${tableMenuName}_${menuId}_${index + 1}_${retryDistributionOptionName}_${optionId}`}
                                                label={t("releases.downloadTakeDownLogs")}
                                                disabled={!canDo(user, [mediaReadPermission])}
                                            />
                                        </Link>
                                    )}
                                    {(client.isApple ||
                                        client.enableNewXmlTemplate ||
                                        client.name?.toLowerCase().includes("facebook")) &&
                                        !isTakedown &&
                                        distributionAction !== videoDistributionAction && (
                                            <>
                                                <Divider />
                                                <MenuItem
                                                    icon={<ClearIcon />}
                                                    id={`${tableMenuName}_${menuId}_${index + 2}_${retryDistributionOptionName}_${optionId}`}
                                                    label={t("actions.takeDown")}
                                                    disabled={
                                                        progress !== 100 ||
                                                        status === "waiting" ||
                                                        !canDo(user, [mediaCreatePermission])
                                                    }
                                                    isDelete
                                                    onClick={() =>
                                                        setTakeDownModalData({
                                                            ...takeDownModalData,
                                                            id: distributionId,
                                                            open: true,
                                                        })
                                                    }
                                                />
                                            </>
                                        )}
                                </TableMenu>
                            </StyledTableCell>
                        </TableRow>
                    ),
                )}
            </TableList>
        </Box>
    );
};

export default DistributionsList;

DistributionsList.propTypes = { releaseId: PropTypes.string };
